import { useApi } from '@renderer/chat'
import { getChatApiUrl } from '@renderer/chat/api/v1Client'
import { getBackendHostAccessError } from '@shared/utils/ChatBackendUtils'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

export type HybridSearchResult = {
  id: string
  score: number
  itemId: string | null
  parentId: string | null
  bundleKey: string | null
  source: string | null
  modality: string | null
  unitKind: string | null
  ts: string | null
  tsEnd: string | null
  createdTs: string | null
  editedTs: string | null
  entityIds: Array<string>
  // Optional: the app and gateway deploy independently, so an older gateway may omit it.
  involvedReferenceIds?: Array<string>
  // The record's own address, e.g. `highlight://audio-notes/<id>`.
  canonicalUrl?: string | null
  rendering: string | null
  // Optional while the app and gateway roll independently. Offsets are UTF-16 positions in rendering.
  highlights?: Array<{
    text: string
    fragmentRange: [number, number]
    matchRanges: Array<[number, number]>
  }>
}

/** Mirror of the gateway's EntityIndexEntryZ: token -> entity id -> positions into `results`. */
export type EntityIndexEntry = {
  token: string
  entityId: string
  resultIndices: Array<number>
}

export type SourceIngestState = 'fresh' | 'backfilling' | 'disconnected' | 'terminal_failure'

export type HybridSearchMode = 'fts' | 'semantic'

export type SourceWatermark = {
  source: string
  ingestState: SourceIngestState
  freshUpTo: string | null
  updatedAt: string
}

export type HybridSearchResponse = {
  results: Array<HybridSearchResult>
  namespace: string | null
  reason: 'no-ingestion-namespace' | 'namespace-not-found' | 'target-unavailable' | null
  // Optional: the app and gateway deploy independently, so an older gateway may omit them.
  entities?: Array<EntityIndexEntry>
  sourceWatermarks?: Array<SourceWatermark>
}

export type HybridSearchParams = {
  query: string
  mode?: HybridSearchMode
  source?: string
  modality?: string
}

export function useHybridSearch(params: HybridSearchParams) {
  const { post } = useApi()
  const query = params.query.trim()
  const mode = params.mode ?? 'fts'
  const source = params.source?.trim() || undefined
  const modality = params.modality?.trim() || undefined

  return useQuery<HybridSearchResponse, Error>({
    queryKey: ['hybrid-search', query, mode, source ?? null, modality ?? null],
    enabled: query.length > 0,
    // Keeps the last results on screen while the next query loads, so typing doesn't flash the list empty.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const resp = await post('hybrid-search/search', JSON.stringify({ query, mode, source, modality }), {
        version: 'v1',
        headers: { 'Content-Type': 'application/json' },
      }).catch((error: unknown) => {
        throw searchFailed(error instanceof Error ? error.message : String(error))
      })
      if (!resp.ok) {
        // statusText is empty over HTTP/2, so build a message that always says
        // something — and name the backend, since "wrong backend" is the most
        // common failure for this dev surface. The gateway emits both bare-string
        // and structured `{ error: { code, message } }` bodies (429, plan rejections).
        let detail = ''
        try {
          const body = (await resp.json()) as { error?: string | { code?: string; message?: string } }
          const message = typeof body?.error === 'string' ? body.error : body?.error?.message
          if (message) detail = ` — ${message}`
        } catch {
          // non-JSON error body; status alone will have to do
        }
        throw searchFailed(`HTTP ${resp.status}${detail}`)
      }
      const json = (await resp.json()) as { success: boolean; data: HybridSearchResponse }
      if (!json.success) {
        throw searchFailed('backend returned success=false')
      }
      return json.data
    },
  })
}

/** A missing Access login is the cause of any failure while it lasts, so it replaces the raw reason. */
function searchFailed(reason: string): Error {
  return new Error(`Search failed: ${getBackendHostAccessError() || reason} (backend: ${getChatApiUrl()})`)
}
